import { useState } from "react";
import { X } from "lucide-react";
import { Input } from "./ui/input";
import { MobileSheet } from "./MobileSheet";
import { useIsMobile } from "../hooks/useIsMobile";

// Shared mobile-safe autocomplete shell used by every "search + pick from a list" control.
//  - Desktop / tablet with room: inline popover (Input + absolute dropdown) — unchanged UX.
//  - Phone / no vertical room: a bottom Drawer with the search field pinned at the TOP and a
//    scrollable results area below it. The search stays visible above the on-screen keyboard,
//    and the trigger NEVER raises the keyboard (global NO-AUTOFOCUS rule) — it only opens the
//    sheet; the keyboard appears solely after an explicit tap on the search field.
//
// `children` is a render-prop: (query, { close, clearQuery }) => option nodes.
//  - single-select options should call `close()` after selecting;
//  - multi-select options should call `clearQuery()` (keep the sheet open to add more).
export function SmartSearchPicker({
  testId, placeholder = "Cerca…", displayLabel = "", disabled,
  mobileTitle = "Seleziona", hint, onClear, children, flat = false,
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const close = () => { setOpen(false); setQ(""); };
  // Option activation must use onClick on touch: inside the vaul Drawer, `mousedown` +
  // preventDefault gets swallowed (vaul owns pointer/drag), so the synthetic mouse event never
  // fires and taps do nothing. On desktop we keep onMouseDown+preventDefault so selecting an
  // option does not blur/close the input (needed for multi-select "keep open").
  const optionProps = (fn) => ((isMobile || flat)
    ? { onClick: (e) => { e.preventDefault(); fn(); } }
    : { onMouseDown: (e) => { e.preventDefault(); fn(); } });
  const helpers = { close, clearQuery: () => setQ(""), optionProps };
  const showClear = !!(onClear && displayLabel);

  // Flat mode: no trigger, no own overlay — just a search field + always-visible scrollable list.
  // Used when the caller already provides the surface (a Popover on desktop, a Drawer on mobile),
  // so "Cambia cavallo" opens straight onto the searchable list. NO AUTOFOCUS.
  if (flat) {
    return (
      <div>
        <Input data-testid={testId ? `${testId}-search` : undefined} autoFocus={false} value={q}
          placeholder={placeholder} autoComplete="off" disabled={disabled}
          onChange={(e) => setQ(e.target.value)} />
        {hint && <p className="text-[11px] text-[#8C6D4F] mt-1">{hint}</p>}
        <div className="mt-2 max-h-[52vh] overflow-y-auto rounded-lg border border-[#E5DEC9] bg-white"
          data-testid={testId ? `${testId}-list` : undefined}>
          {children(q, helpers)}
        </div>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div>
        <div className="relative flex items-center">
          <Input data-testid={testId} readOnly value={displayLabel} placeholder={placeholder}
            disabled={disabled} className={`cursor-pointer ${showClear ? "pr-8" : ""}`}
            onFocus={(e) => e.target.blur()}
            onClick={() => { if (!disabled) { setQ(""); setOpen(true); } }} />
          {showClear && (
            <button type="button" data-testid={testId ? `${testId}-clear` : undefined}
              onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="absolute right-2 text-[#8C7A5E] p-1"><X className="h-3.5 w-3.5" /></button>
          )}
        </div>
        <MobileSheet open={open} onOpenChange={(o) => { if (!o) close(); }} title={mobileTitle}
          testId={testId ? `${testId}-sheet` : undefined}>
          <div className="px-4 pb-2 shrink-0">
            <Input data-testid={testId ? `${testId}-search` : undefined} autoFocus={false}
              value={q} placeholder={placeholder} autoComplete="off"
              onChange={(e) => setQ(e.target.value)} />
            {hint && <p className="text-[11px] text-[#8C6D4F] mt-1">{hint}</p>}
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto px-2" data-testid={testId ? `${testId}-list` : undefined}
            style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
            {children(q, helpers)}
          </div>
        </MobileSheet>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <Input data-testid={testId} value={open ? q : displayLabel} placeholder={placeholder}
          autoComplete="off" disabled={disabled}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => { setQ(""); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 180)} />
        {showClear && !open && (
          <button type="button" data-testid={testId ? `${testId}-clear` : undefined}
            onMouseDown={(e) => { e.preventDefault(); onClear(); }}
            className="shrink-0 text-[#8C7A5E] px-1"><X className="h-3.5 w-3.5" /></button>
        )}
      </div>
      {open && !disabled && (
        <div data-testid={testId ? `${testId}-list` : undefined}
          className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-[#E5DEC9] bg-white shadow-lg">
          {children(q, helpers)}
        </div>
      )}
    </div>
  );
}
